import { PersonType } from '../Types/enums';
import type { IncidentRosterPerson } from '../Types/Incident';

/** Human-readable label for a person type enum value. */
export const getPersonTypeLabel = (personType?: string | null): string | undefined => {
  if (!personType) return undefined;
  const lower = String(personType).toLowerCase().trim();
  switch (lower) {
    case PersonType.Employee: return 'Employee';
    case PersonType.Visitor: return 'Visitor';
    case PersonType.Contractor: return 'Contractor';
    case PersonType.Tenant: return 'Tenant';
    case PersonType.Unlisted: return 'Manual Add';
    default: {
      // If it's a non-numeric readable string, return as-is (capitalize first letter)
      if (/^\d+$/.test(lower)) return undefined;
      return personType.charAt(0).toUpperCase() + personType.slice(1);
    }
  }
};

/** Human-readable label for badge type values. */
export const getBadgeTypeLabel = (badgeType?: string | null): string | undefined => {
  if (!badgeType) return undefined;
  const lower = badgeType.toLowerCase().trim();
  switch (lower) {
    case 'employeebadge': return 'Employee badge';
    case 'tenantbadge': return 'Tenant badge';
    case 'contractorbadge': return 'Contractor badge';
    case 'standard': return 'Standard badge';
    case 'tenant': return 'Tenant badge';
    default:
      return badgeType.charAt(0).toUpperCase() + badgeType.slice(1);
  }
};

/** Human-readable label for roster source values. */
export const getRosterSourceLabelByValue = (rosterSource?: string | null): string | undefined => {
  if (!rosterSource) return undefined;
  const lower = rosterSource.toLowerCase().trim();
  switch (lower) {
    case 'badgedata': return 'Badge Data';
    case 'lobbytrack': return 'Lobby Track';
    case 'assignedoffice': return 'Assigned Office';
    case 'manualadd': return 'Manual Add';
    default:
      return rosterSource.charAt(0).toUpperCase() + rosterSource.slice(1);
  }
};

/** Subtitle line for person display: "Tenant · CompanyName" or practice/company for others. */
export const getPersonSubtitle = (person: IncidentRosterPerson): string | undefined => {
  const isTenant = person.personType?.toLowerCase().trim() === PersonType.Tenant;
  if (isTenant) {
    return person.companyName ? `Tenant \u00B7 ${person.companyName}` : 'Tenant';
  }
  if (person.companyName) return person.companyName;
  if (person.practice) return person.practice;
  return undefined;
};

/** Returns true when contact info (email or phone) is available. */
export const hasContactInfo = (person: IncidentRosterPerson): boolean => {
  return Boolean(person.email) || Boolean(person.phone);
};
